import { Alert, MenuItem as MuiMenuItem, Popper, Select, SelectChangeEvent } from "@mui/material";
import { buildingQueryKey } from "ApiLayer/Building/queryBuildingModel";
import type { AgentResultArtifact } from "ApiLayer/Chatbot/queryBuildingChatHistory";
import { useExportAgentArtifactMutation } from "ApiLayer/Chatbot/queryBuildingChatHistory";
import { AIGenerate, ExportIcon } from "components/atoms/Icon";
import NestedDropdown from "components/atoms/NestedDropdown/NestedDropdown";
import { Header, Paragraph } from "components/atoms/Typography";
import DangerousModal from "components/molecules/DangerousModal";
import Sidebar from "components/molecules/Sidebar";
import MUITheme from "components/MUITheme";
import AttachedDocuments from "components/organisms/Chatbot/AttachedDocuments/AttachedDocuments";
import DocumentSelectionModal from "components/organisms/Chatbot/DocumentSelectionModal/DocumentSelectionModal";
import ThoughtsSection from "components/organisms/Chatbot/ThoughtsSection/ThoughtsSection";
import CreateCustomPlanModal from "components/organisms/Modals/CreateCustomPlanModal/createCustomPlanModal";
import HoldingPediodModal from "components/organisms/Modals/holdingPeriodModal/holdingPeriodModal";
import DOMPurify from "dompurify";
import { useSelectedBuildingModelCarbonReductionPlan } from "hooks/Building/useBuildingModelCarbonReductionPlan";
import useChatbot from "hooks/Chatbot/useChatbot";
import { measureDesignScenarioToMeasureInput } from "hooks/CustomPlan/useCustomPlanUpdate.utils";
import { useAccessToken } from "hooks/useAccessToken";
import { useBuildingModelUid } from "hooks/useBuildingModelUid";
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQueries, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import useBuildingSidebarState from "recoilStore/CustomPlan/useBuildingSidebarState";
import useMeasureSidebarState from "recoilStore/CustomPlan/useMeasureSidebarState";
import { BACKEND_DOMAIN } from "utils/constants";
import { equipmentTopologyArtifactToSurveyPrefill } from "utils/equipmentTopologyTableToSurveyPrefill";
import { buildRequestHeaders } from "utils/helpers";
import { DocumentCitationTooltipContent } from "../Citation/DocumentCitationTooltip";
import {
    DOMPURIFY_ALLOWED_ATTRIBUTES,
    GEMINI_INVALID_RESPONSE_USER_MESSAGE,
    MEASURE_DESIGN_PRIOR_AGENTS_DISCLAIMER,
    MenuItem,
} from "./ChatbotSidebar.constants";

import styles from "./ChatbotSidebar.module.scss";

async function fetchAgentArtifactJson(
    accessToken: string,
    params: { buildingModelUid: string; runUid: string; agent: string }
): Promise<AgentResultArtifact> {
    const searchParams = new URLSearchParams({
        building_model_uid: params.buildingModelUid,
        run_uid: params.runUid,
        agent: params.agent,
    });
    const url = `${BACKEND_DOMAIN}/single-building-chatbot/agent-results/export?${searchParams}`;
    const response = await fetch(url, {
        method: "GET",
        headers: buildRequestHeaders(accessToken),
    });
    if (!response.ok) {
        throw new Error(`Error ${response.status}`);
    }
    return (await response.json()) as AgentResultArtifact;
}

interface ChatbotSidebarProps {
    handleClose: () => void;
    sidebarOpen: boolean;
    title: string;
    page: PageWithChatbot;
}

interface BuildingContext {
    buildingModelUid: string | null;
    buildingModel?: Building;
    bmcrp?: BuildingModelCarbonReductionPlan;
}

function ChatHeader({ title }: { title: string }) {
    return (
        <div className={styles.chatHeader}>
            <div className={styles.firstContainer}>
                <AIGenerate color="white" />
            </div>
            <Header
                size="small"
                weight="medium"
                style={{
                    margin: 0,
                    lineHeight: "1.2",
                    color: "var(--audette-purple-400)",
                }}
            >
                {title}
            </Header>
        </div>
    );
}

const MEASURE_DESIGN_SCENARIO_LABELS = [
    "💰 Profitability Focus",
    "🌱 Carbon Reduction Focus",
    "⚖️ Balanced Goals",
] as const;

const MEASURE_DESIGN_SCENARIO_KEYS = ["profitabilityFocused", "carbonReductionFocused", "balanced"] as const;

/** Section HTML for the structured layout comes only from the export artifact: `data.scenarios[*].narrativeHtml`. */
function measureDesignNarrativeHtmls(exportData: Record<string, unknown> | undefined): string[] | null {
    const scenarios = exportData?.scenarios as Record<string, unknown> | undefined;
    if (!scenarios || Array.isArray(scenarios)) return null;
    const parts = MEASURE_DESIGN_SCENARIO_KEYS.map((k) => {
        const blob = scenarios[k];
        if (blob == null || typeof blob !== "object" || Array.isArray(blob)) {
            return null;
        }
        const html = (blob as Record<string, unknown>).narrativeHtml;
        return typeof html === "string" ? html : null;
    });
    return parts.every((h): h is string => h !== null) ? parts : null;
}

type MeasureDesignScenarioKey = (typeof MEASURE_DESIGN_SCENARIO_KEYS)[number];

interface AddToPlanRowProps {
    messageId: string;
    scenarioKey: MeasureDesignScenarioKey;
    scenarios: Record<string, unknown>;
    customPlans: BuildingModelCarbonReductionPlan[];
    getSelectedPlanId: (messageId: string) => string;
    setSelectedPlanId: (messageId: string, planId: string) => void;
    handleAddToPlan: (messageId: string, scenarioKey: string, scenarios: Record<string, unknown>) => void;
    openAddMeasureToNewCustomPlan: (scenarioKey: MeasureDesignScenarioKey, scenarios: Record<string, unknown>) => void;
    canAddToNewPlan: boolean;
    newPlanButtonLabel?: string;
}

function AddToPlanRow({
    messageId,
    scenarioKey,
    scenarios,
    customPlans,
    getSelectedPlanId,
    setSelectedPlanId,
    handleAddToPlan,
    openAddMeasureToNewCustomPlan,
    canAddToNewPlan,
    newPlanButtonLabel = "Add to new custom plan",
}: AddToPlanRowProps) {
    if (customPlans.length > 0) {
        return (
            <div className={styles.addToPlanRow}>
                <div className={styles.addToPlanPlanSelector}>
                    <Paragraph size="small" className={styles.addToPlanLabel}>
                        In:
                    </Paragraph>
                    <MUITheme primaryColor="#EB03AD">
                        <Select
                            value={getSelectedPlanId(messageId)}
                            onChange={(e: SelectChangeEvent<string>) => setSelectedPlanId(messageId, e.target.value)}
                            displayEmpty
                            size="small"
                            className={styles.addToPlanSelect}
                        >
                            {customPlans.map((plan: BuildingModelCarbonReductionPlan) => (
                                <MuiMenuItem key={plan.id} value={plan.id}>
                                    {plan.name}
                                </MuiMenuItem>
                            ))}
                        </Select>
                    </MUITheme>
                </div>
                <button
                    type="button"
                    className={styles.addToPlanButton}
                    onClick={() => handleAddToPlan(messageId, scenarioKey, scenarios)}
                >
                    Add Measure
                </button>
            </div>
        );
    }
    if (canAddToNewPlan) {
        return (
            <div className={styles.addToPlanRow}>
                <button
                    type="button"
                    className={styles.addToPlanButton}
                    onClick={() => openAddMeasureToNewCustomPlan(scenarioKey, scenarios)}
                >
                    {newPlanButtonLabel}
                </button>
            </div>
        );
    }
    return null;
}

function ChatbotSidebarContent({
    handleClose,
    sidebarOpen,
    title,
    page,
    buildingContext,
}: ChatbotSidebarProps & { buildingContext: BuildingContext }) {
    const { buildingModelUid, buildingModel } = buildingContext;
    const queryClient = useQueryClient();
    const accessToken = useAccessToken();
    const { state: buildingSidebarState, setState: setBuildingSidebarState } = useBuildingSidebarState();
    const { setState: setMeasureSidebarState } = useMeasureSidebarState();
    const navigate = useNavigate();

    const [isExpanded, setIsExpanded] = useState(true);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);
    const [documentModalOpen, setDocumentModalOpen] = useState(false);
    const [createCustomPlanModalOpen, setCreateCustomPlanModalOpen] = useState(false);
    const [pendingScenarioForNewPlan, setPendingScenarioForNewPlan] = useState<{
        scenarioKey: MeasureDesignScenarioKey;
        scenarios: Record<string, unknown>;
    } | null>(null);
    const [hoveredCitation, setHoveredCitation] = useState<{
        anchorEl: HTMLElement;
        blockText: string;
        title: string;
        pageNumber: string;
    } | null>(null);
    const [selectedPlanIdByMessageId, setSelectedPlanIdByMessageId] = useState<Record<string, string>>({});

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const {
        messages,
        currentMessage,
        setCurrentMessage,
        handleSendMessage,
        handleAgentSelect,
        handleClearHistory,
        handleDownloadHistory,
        isLoading,
        isLoadingPortfolio,
        isHistoryLoading,
        isClearingHistory,
        isExportingHistory,
        messagesEndRef,
        holdingPeriod,
        setHoldingPeriod,
    } = useChatbot({ page, sidebarOpen, buildingModelUid });

    const measureDesignMessages = messages.filter(
        (m) => m.agentId === "measure_design" && m.exportRunUid && m.type === "receive"
    );
    const measureDesignExportRequests = useMemo(
        () =>
            measureDesignMessages.map((message) => ({
                runUid: message.exportRunUid as string,
                agent: message.agentId as string,
            })),
        [measureDesignMessages]
    );
    const artifactQueries = useQueries(
        measureDesignExportRequests.map((request) => ({
            queryKey: ["agentExport", request.runUid, buildingModelUid] as const,
            queryFn: () =>
                fetchAgentArtifactJson(accessToken, {
                    buildingModelUid: buildingModelUid!,
                    runUid: request.runUid,
                    agent: request.agent,
                }),
            enabled: page === "building" && !!buildingModelUid,
        }))
    );

    const artifactDataByRunUid = artifactQueries.reduce<Record<string, Record<string, unknown>>>(
        (acc, queryResult, i) => {
            const runUid = measureDesignExportRequests[i]?.runUid;
            const artifact = queryResult.data as AgentResultArtifact | undefined;
            if (!runUid || artifact == null) return acc;
            acc[runUid] = artifact.data;
            return acc;
        },
        {}
    );

    const { mutate: exportAgentArtifact, isLoading: isExportingAgentArtifact } = useExportAgentArtifactMutation();
    const { mutate: exportAgentResultJson, isLoading: isLoadingTopologyPrefill } = useExportAgentArtifactMutation();
    const [exportingRunUid, setExportingRunUid] = useState<string | null>(null);
    const [prefillingRunUid, setPrefillingRunUid] = useState<string | null>(null);

    const { buildingModelCarbonReductionPlanId } = useParams();

    const plans = buildingModel?.carbonReductionPlans ?? [];
    const customPlans = useMemo(
        () => plans.filter((plan: BuildingModelCarbonReductionPlan) => plan.planType === "custom"),
        [plans]
    );
    const customPlanById = useMemo(
        () =>
            customPlans.reduce<Record<string, BuildingModelCarbonReductionPlan>>((acc, plan) => {
                acc[plan.id] = plan;
                return acc;
            }, {}),
        [customPlans]
    );

    const getSelectedPlanId = (messageId: string) => {
        const explicit = selectedPlanIdByMessageId[messageId];
        if (explicit && customPlanById[explicit]) return explicit;
        return customPlans[0]?.id ?? "";
    };
    const setSelectedPlanId = (messageId: string, planId: string) =>
        setSelectedPlanIdByMessageId((prev) => ({ ...prev, [messageId]: planId }));

    const applyMeasureToCustomPlan = (
        plan: BuildingModelCarbonReductionPlan,
        scenarioKey: string,
        scenarios: Record<string, unknown>
    ) => {
        const scenario = scenarios[scenarioKey];
        if (scenario == null || typeof scenario !== "object" || !buildingModelUid) return;
        const measureInput = measureDesignScenarioToMeasureInput(
            scenario as Parameters<typeof measureDesignScenarioToMeasureInput>[0]
        );
        setBuildingSidebarState({
            ...buildingSidebarState,
            currentCustomPlanDetails: {
                id: plan.id,
                name: plan.name,
                plan: plan.plan,
            },
        });
        if (buildingModelCarbonReductionPlanId !== plan.id) {
            navigate(`/buildings/${buildingModelUid}/plans/${plan.id}/baseline`);
        }
        setMeasureSidebarState((prev) => ({
            ...prev,
            isCreateOpen: true,
            createMeasurePrefill: measureInput,
        }));
    };

    const handleAddToPlan = (messageId: string, scenarioKey: string, scenarios: Record<string, unknown>) => {
        const selectedPlanId = getSelectedPlanId(messageId);
        if (!selectedPlanId) return;
        const selectedPlan = customPlanById[selectedPlanId];
        if (!selectedPlan) return;
        applyMeasureToCustomPlan(selectedPlan, scenarioKey, scenarios);
    };

    const openAddMeasureToNewCustomPlan = (
        scenarioKey: MeasureDesignScenarioKey,
        scenarios: Record<string, unknown>
    ) => {
        setPendingScenarioForNewPlan({ scenarioKey, scenarios });
        setCreateCustomPlanModalOpen(true);
    };

    const handleNewCustomPlanCreatedForChat = async (newPlanId: string) => {
        const pending = pendingScenarioForNewPlan;
        setPendingScenarioForNewPlan(null);

        if (pending && buildingModelUid) {
            await queryClient.refetchQueries({ queryKey: [...buildingQueryKey, buildingModelUid] });
            const refreshed = queryClient.getQueryData<Building>([...buildingQueryKey, buildingModelUid]);
            const plan = refreshed?.carbonReductionPlans?.find(
                (p: BuildingModelCarbonReductionPlan) => p.id === newPlanId
            );
            if (plan) {
                applyMeasureToCustomPlan(plan, pending.scenarioKey, pending.scenarios);
                return;
            }
        }

        navigate(`/buildings/${buildingModelUid}/plans/${newPlanId}/custom/baseline`);
    };

    const incentiveItems: MenuItem[] = [
        { id: "incentive_capital", displayValue: "CapEx Reduction Analysis" },
        { id: "incentive_revenue", displayValue: "New Revenue Streams Analysis" },
        { id: "incentive_tax", displayValue: "Tax Analysis" },
        { id: "incentive_operational", displayValue: "OpEx Savings Analysis" },
        { id: "incentive_financing", displayValue: "Financing Analysis" },
    ];

    const handleReset = () => setResetConfirmationOpen(true);

    const handleConfirmReset = () => {
        handleClearHistory({
            onSuccess: () => setResetConfirmationOpen(false),
            onError: () => setResetConfirmationOpen(false),
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCurrentMessage(e.target.value);
        resizeTextArea();
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const resizeTextArea = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    return (
        <>
            <Sidebar
                onCancel={handleClose}
                sidebarOpen={sidebarOpen}
                title={<ChatHeader title={title} />}
                onReset={handleReset}
                onDownload={page === "building" ? handleDownloadHistory : undefined}
                isDownloading={isExportingHistory}
                noBodyPadding={true}
                isExpanded={isExpanded}
                canBeExpanded={true}
                setIsExpanded={setIsExpanded}
                positionBelowHeader={true}
            >
                <div className={styles.container}>
                    <div
                        className={styles.messagesContainer}
                        onMouseOver={(e) => {
                            const link = (e.target as HTMLElement).closest(
                                ".document-citation-link"
                            ) as HTMLElement | null;
                            if (link) {
                                setHoveredCitation({
                                    anchorEl: link,
                                    blockText: link.getAttribute("data-block-text") || "",
                                    title: link.getAttribute("title") || "",
                                    pageNumber: link.getAttribute("data-page-number") || "",
                                });
                            }
                        }}
                        onFocus={(e) => {
                            const link = (e.target as HTMLElement).closest(
                                ".document-citation-link"
                            ) as HTMLElement | null;
                            if (link) {
                                setHoveredCitation({
                                    anchorEl: link,
                                    blockText: link.getAttribute("data-block-text") || "",
                                    title: link.getAttribute("title") || "",
                                    pageNumber: link.getAttribute("data-page-number") || "",
                                });
                            }
                        }}
                        onMouseOut={(e) => {
                            const link = (e.relatedTarget as HTMLElement)?.closest?.(".document-citation-link");
                            if (!link) {
                                setHoveredCitation(null);
                            }
                        }}
                        onBlur={(e) => {
                            const link = (e.relatedTarget as HTMLElement)?.closest?.(".document-citation-link");
                            if (!link) {
                                setHoveredCitation(null);
                            }
                        }}
                    >
                        {messages.map((message) => {
                            const exportData = message.exportRunUid
                                ? artifactDataByRunUid[message.exportRunUid]
                                : undefined;
                            const measureDesignHtmlSections =
                                message.agentId === "measure_design" ? measureDesignNarrativeHtmls(exportData) : null;
                            const isStructuredMeasureDesign =
                                message.type === "receive" &&
                                message.agentId === "measure_design" &&
                                message.exportRunUid &&
                                measureDesignHtmlSections != null &&
                                measureDesignHtmlSections.length === 3;

                            const qi = message.exportRunUid
                                ? measureDesignExportRequests.findIndex((r) => r.runUid === message.exportRunUid)
                                : -1;
                            const artifactExportQuery = qi >= 0 ? artifactQueries[qi] : undefined;
                            const artifactExportStillLoading =
                                page === "building" &&
                                !!buildingModelUid &&
                                !!artifactExportQuery &&
                                (artifactExportQuery.isLoading || artifactExportQuery.isFetching);

                            const showMeasureDesignArtifactFallback =
                                message.type === "receive" &&
                                message.agentId === "measure_design" &&
                                !!message.exportRunUid &&
                                !isStructuredMeasureDesign &&
                                page === "building";

                            return (
                                <div key={message.id} className={`${styles.messageWrapper} ${styles[message.type]}`}>
                                    {message.thoughts && message.thinkingDuration && (
                                        <ThoughtsSection
                                            thoughts={message.thoughts}
                                            thinkingDuration={message.thinkingDuration}
                                        />
                                    )}
                                    {message.isThinking && (
                                        <div className={styles.thinkingIndicator}>
                                            <div className={styles.thinkingDot} />
                                            <div className={styles.thinkingDot} />
                                            <div className={styles.thinkingDot} />
                                        </div>
                                    )}
                                    {message.activeToolCalls && message.activeToolCalls.length > 0 && (
                                        <div className={styles.toolCallList}>
                                            {message.activeToolCalls.map((name) => (
                                                <div key={name} className={styles.toolCallIndicator}>
                                                    <span className={styles.toolCallIcon}>⚙</span>
                                                    <span className={styles.toolCallLabel}>
                                                        {name.replace(/_/g, " ")}
                                                    </span>
                                                    <div className={styles.toolCallSpinner} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {message.isFormattingResponse && (
                                        <div className={styles.formattingResponseIndicator}>
                                            <div className={styles.toolCallSpinner} />
                                            <span>Formatting response...</span>
                                        </div>
                                    )}
                                    {showMeasureDesignArtifactFallback && artifactExportStillLoading && (
                                        <div className={styles.thinkingIndicator}>
                                            <div className={styles.thinkingDot} />
                                            <div className={styles.thinkingDot} />
                                            <div className={styles.thinkingDot} />
                                        </div>
                                    )}
                                    {showMeasureDesignArtifactFallback && !artifactExportStillLoading && (
                                        <div className={styles.measureDesignArtifactStatus}>
                                            <Alert severity="info" className={styles.measureDesignPriorAgentsAlert}>
                                                <Paragraph size="small" style={{ margin: 0 }}>
                                                    {GEMINI_INVALID_RESPONSE_USER_MESSAGE}
                                                </Paragraph>
                                            </Alert>
                                        </div>
                                    )}
                                    {isStructuredMeasureDesign ? (
                                        <div className={styles.measureDesignStructured}>
                                            {exportData?.show_prior_agents_disclaimer === true && (
                                                <Alert
                                                    severity="warning"
                                                    className={styles.measureDesignPriorAgentsAlert}
                                                >
                                                    {MEASURE_DESIGN_PRIOR_AGENTS_DISCLAIMER}
                                                </Alert>
                                            )}
                                            {measureDesignHtmlSections!.map((sectionHtml, idx) => {
                                                const key = MEASURE_DESIGN_SCENARIO_KEYS[idx];
                                                const scenariosRecord = (exportData?.scenarios ?? {}) as Record<
                                                    string,
                                                    unknown
                                                >;
                                                const scenarioPayload = scenariosRecord[key] as
                                                    | { name?: string }
                                                    | undefined;
                                                const measureName = scenarioPayload?.name?.trim() ?? "";
                                                const titleLine = measureName
                                                    ? `${MEASURE_DESIGN_SCENARIO_LABELS[idx]}: ${measureName}`
                                                    : MEASURE_DESIGN_SCENARIO_LABELS[idx];
                                                return (
                                                    <div
                                                        key={MEASURE_DESIGN_SCENARIO_LABELS[idx]}
                                                        className={styles.addToPlanMeasureSection}
                                                    >
                                                        <div className={styles.measureDesignSectionTitle}>
                                                            <Header size="small" weight="medium">
                                                                {titleLine}
                                                            </Header>
                                                        </div>
                                                        <div
                                                            // eslint-disable-next-line react/no-danger
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(sectionHtml, {
                                                                    ADD_ATTR: DOMPURIFY_ALLOWED_ATTRIBUTES,
                                                                    ADD_TAGS: ["svg", "path"],
                                                                } as DOMPurify.Config),
                                                            }}
                                                        />
                                                        {page === "building" &&
                                                            buildingModelUid &&
                                                            scenarioPayload != null &&
                                                            typeof scenarioPayload === "object" && (
                                                                <AddToPlanRow
                                                                    messageId={message.id}
                                                                    scenarioKey={key}
                                                                    scenarios={scenariosRecord}
                                                                    customPlans={customPlans}
                                                                    getSelectedPlanId={getSelectedPlanId}
                                                                    setSelectedPlanId={setSelectedPlanId}
                                                                    handleAddToPlan={handleAddToPlan}
                                                                    openAddMeasureToNewCustomPlan={
                                                                        openAddMeasureToNewCustomPlan
                                                                    }
                                                                    canAddToNewPlan={
                                                                        !!(
                                                                            buildingModel?.reportedCrp
                                                                                ?.carbonReductionPlanUid ||
                                                                            buildingModel?.baselineCrp
                                                                                ?.carbonReductionPlanUid
                                                                        )
                                                                    }
                                                                    newPlanButtonLabel="Add this measure to new custom plan"
                                                                />
                                                            )}
                                                    </div>
                                                );
                                            })}
                                            {typeof exportData?.web_citations_html === "string" &&
                                                exportData.web_citations_html && (
                                                    <div
                                                        // eslint-disable-next-line react/no-danger
                                                        dangerouslySetInnerHTML={{
                                                            __html: DOMPurify.sanitize(exportData.web_citations_html, {
                                                                ADD_ATTR: DOMPURIFY_ALLOWED_ATTRIBUTES,
                                                                ADD_TAGS: ["svg", "path"],
                                                            } as DOMPurify.Config),
                                                        }}
                                                    />
                                                )}
                                            {typeof exportData?.search_entry_point_html === "string" &&
                                                exportData.search_entry_point_html && (
                                                    <div
                                                        // eslint-disable-next-line react/no-danger
                                                        dangerouslySetInnerHTML={{
                                                            __html: DOMPurify.sanitize(
                                                                exportData.search_entry_point_html,
                                                                {
                                                                    ADD_ATTR: DOMPURIFY_ALLOWED_ATTRIBUTES,
                                                                    ADD_TAGS: ["svg", "path"],
                                                                } as DOMPurify.Config
                                                            ),
                                                        }}
                                                    />
                                                )}
                                        </div>
                                    ) : (
                                        <div
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{
                                                __html: DOMPurify.sanitize(message.text, {
                                                    ADD_ATTR: DOMPURIFY_ALLOWED_ATTRIBUTES,
                                                    ADD_TAGS: ["svg", "path"],
                                                } as DOMPurify.Config),
                                            }}
                                        />
                                    )}
                                    {message.type === "receive" &&
                                        message.exportRunUid &&
                                        message.agentId &&
                                        buildingModelUid && (
                                            <div className={styles.exportButtonWrap}>
                                                {message.agentId === "equipment_topology" && (
                                                    <button
                                                        type="button"
                                                        className={styles.standardExportButton}
                                                        disabled={isLoadingTopologyPrefill}
                                                        onClick={() => {
                                                            if (!message.exportRunUid || !message.agentId) return;
                                                            setPrefillingRunUid(message.exportRunUid);
                                                            exportAgentResultJson(
                                                                {
                                                                    buildingModelUid,
                                                                    runUid: message.exportRunUid,
                                                                    agent: message.agentId,
                                                                    responseType: "json",
                                                                },
                                                                {
                                                                    onSuccess: (data) => {
                                                                        if (data instanceof Blob) return;
                                                                        const equipmentSurveyPrefill =
                                                                            equipmentTopologyArtifactToSurveyPrefill(
                                                                                data
                                                                            );
                                                                        if (!equipmentSurveyPrefill) return;
                                                                        setBuildingSidebarState({
                                                                            ...buildingSidebarState,
                                                                            isSidebarOpen: true,
                                                                        });
                                                                        window.dispatchEvent(
                                                                            new CustomEvent(
                                                                                "equipment-topology-prefill",
                                                                                {
                                                                                    detail: { equipmentSurveyPrefill },
                                                                                }
                                                                            )
                                                                        );
                                                                    },
                                                                    onSettled: () => setPrefillingRunUid(null),
                                                                }
                                                            );
                                                        }}
                                                    >
                                                        <div className={styles.exportButton}>
                                                            {isLoadingTopologyPrefill &&
                                                                prefillingRunUid === message.exportRunUid && (
                                                                    <div className="loading-spinner" />
                                                                )}
                                                            <span className={styles.exportButtonLabel}>
                                                                Add topology to building
                                                            </span>
                                                        </div>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className={`sidebar-download ${styles.standardExportButton}`}
                                                    title={
                                                        isExportingAgentArtifact
                                                            ? "Exporting..."
                                                            : "Export table as JSON"
                                                    }
                                                    disabled={isExportingAgentArtifact}
                                                    onClick={() => {
                                                        if (!message.exportRunUid || !message.agentId) return;
                                                        setExportingRunUid(message.exportRunUid);
                                                        exportAgentArtifact(
                                                            {
                                                                buildingModelUid,
                                                                runUid: message.exportRunUid,
                                                                agent: message.agentId,
                                                                responseType: "blob",
                                                            },
                                                            {
                                                                onSuccess: (blob) => {
                                                                    if (!(blob instanceof Blob)) return;
                                                                    const url = window.URL.createObjectURL(blob);
                                                                    const a = document.createElement("a");
                                                                    a.href = url;
                                                                    a.download = `${buildingModelUid}_${message.agentId}_${message.exportRunUid}.json`;
                                                                    document.body.appendChild(a);
                                                                    a.click();
                                                                    document.body.removeChild(a);
                                                                    window.URL.revokeObjectURL(url);
                                                                },
                                                                onSettled: () => setExportingRunUid(null),
                                                            }
                                                        );
                                                    }}
                                                >
                                                    {isExportingAgentArtifact &&
                                                    exportingRunUid === message.exportRunUid ? (
                                                        <div className="loading-spinner" />
                                                    ) : (
                                                        <div className={styles.exportButton}>
                                                            <span className={styles.exportButtonIcon}>
                                                                <ExportIcon />
                                                            </span>
                                                            <span className={styles.exportButtonLabel}>.json</span>
                                                        </div>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                </div>
                            );
                        })}
                        {isHistoryLoading && (
                            <div className={`${styles.messageWrapper} ${styles.receive} ${styles.loading}`}>
                                <div className={styles.loadingMessage}>
                                    <div className={styles.loadingSpinner} />
                                    <span>Loading chat history...</span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                    {hoveredCitation &&
                        createPortal(
                            <MUITheme>
                                <Popper
                                    open={true}
                                    anchorEl={hoveredCitation.anchorEl}
                                    placement="top"
                                    modifiers={[
                                        {
                                            name: "offset",
                                            options: {
                                                offset: [0, 8],
                                            },
                                        },
                                        {
                                            name: "flip",
                                            options: {
                                                fallbackPlacements: ["bottom"],
                                            },
                                        },
                                    ]}
                                    style={{ zIndex: 1000 }}
                                >
                                    <div
                                        style={{
                                            backgroundColor: "#ffffff",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "8px",
                                            padding: "12px 16px",
                                            boxShadow:
                                                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                                            maxWidth: "400px",
                                        }}
                                        onMouseEnter={() => {
                                            setHoveredCitation(hoveredCitation);
                                        }}
                                        onMouseLeave={() => {
                                            setHoveredCitation(null);
                                        }}
                                    >
                                        <DocumentCitationTooltipContent
                                            blockText={hoveredCitation.blockText}
                                            title={hoveredCitation.title}
                                            pageNumber={hoveredCitation.pageNumber}
                                        />
                                    </div>
                                </Popper>
                            </MUITheme>,
                            document.body
                        )}

                    {page === "building" && buildingModelUid && (
                        <DocumentSelectionModal
                            isOpen={documentModalOpen}
                            onClose={() => setDocumentModalOpen(false)}
                            onAddDocuments={() => {
                                setDocumentModalOpen(false);
                            }}
                            buildingModelUid={buildingModelUid}
                        />
                    )}

                    <div className={styles.chatTextContainer}>
                        {page === "building" && buildingModelUid && (
                            <div className={styles.analysisChips}>
                                <button
                                    type="button"
                                    className={styles.chip}
                                    disabled={isLoading || isHistoryLoading}
                                    onClick={() => handleAgentSelect("finance")}
                                >
                                    Market Analysis
                                </button>
                                <button
                                    type="button"
                                    className={styles.chip}
                                    disabled={isLoading || isHistoryLoading}
                                    onClick={() => handleAgentSelect("compliance")}
                                >
                                    Compliance Analysis
                                </button>
                                <button
                                    type="button"
                                    className={styles.chip}
                                    disabled={isLoading || isHistoryLoading}
                                    onClick={() => handleAgentSelect("equipment_topology")}
                                >
                                    Equipment Analysis
                                </button>
                                <button
                                    type="button"
                                    className={styles.chip}
                                    disabled={isLoading || isHistoryLoading}
                                    onClick={() => handleAgentSelect("measure_design")}
                                >
                                    Measure Design
                                </button>
                                <div className={styles.chipDropdownWrapper}>
                                    <NestedDropdown
                                        trigger={
                                            <span className={`${styles.chip} ${styles.chipWithArrow}`}>
                                                Incentives Analyses
                                                <svg
                                                    width="10"
                                                    height="6"
                                                    viewBox="0 0 10 6"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        d="M1 1L5 5L9 1"
                                                        stroke="currentColor"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </span>
                                        }
                                        items={incentiveItems}
                                        onSelect={(key) => handleAgentSelect(key)}
                                        disabled={isLoading || isHistoryLoading}
                                    />
                                </div>
                            </div>
                        )}

                        <div className={styles.innerContainer}>
                            {page === "building" && buildingModelUid && (
                                <button
                                    type="button"
                                    className={styles.addDocumentsButton}
                                    disabled={isLoading || isHistoryLoading}
                                    title="Attach documents to chat"
                                    aria-label="Attach documents"
                                    onClick={() => setDocumentModalOpen(true)}
                                >
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M21.44 11.05L12.25 20.24C11.1242 21.3658 9.5972 21.9983 8.005 21.9983C6.4128 21.9983 4.8858 21.3658 3.76 20.24C2.6342 19.1142 2.0017 17.5872 2.0017 15.995C2.0017 14.4028 2.6342 12.8758 3.76 11.75L12.33 3.18C13.0806 2.42944 14.0991 2.00778 15.16 2.00778C16.2209 2.00778 17.2394 2.42944 17.99 3.18C18.7406 3.93056 19.1622 4.94911 19.1622 6.01C19.1622 7.07089 18.7406 8.08944 17.99 8.84L9.41 17.41C9.03472 17.7853 8.52778 17.9961 8 17.9961C7.47222 17.9961 6.96528 17.7853 6.59 17.41C6.21472 17.0347 6.00389 16.5278 6.00389 16C6.00389 15.4722 6.21472 14.9653 6.59 14.59L15.07 6.11"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            )}
                            <textarea
                                ref={textareaRef}
                                rows={1} // Default browser behavior is 2 rows, this centers the text vertically
                                value={currentMessage}
                                className={styles.chatTextInput}
                                placeholder={isHistoryLoading ? "Loading chat history..." : "Type your message..."}
                                maxLength={1000}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyPress}
                                disabled={isLoading || isHistoryLoading}
                            />
                            <button
                                onClick={handleSendMessage}
                                disabled={!currentMessage.trim() || isHistoryLoading}
                                type="button"
                                className={styles.sendButton}
                            >
                                Send
                            </button>
                        </div>

                        {page === "building" && buildingModelUid && (
                            <AttachedDocuments
                                buildingModelUid={buildingModelUid}
                                onAddDocument={() => setDocumentModalOpen(true)}
                            />
                        )}
                    </div>

                    <div className={styles.disclaimer}>
                        This conversation was generated using Google AI. Google AI models may make mistakes, so
                        double-check outputs.
                    </div>
                </div>
            </Sidebar>

            {page === "portfolio" && (
                <HoldingPediodModal
                    modalIsOpen={modalIsOpen}
                    onCancel={() => setModalIsOpen(false)}
                    setModalIsOpen={setModalIsOpen}
                    setHoldingPeriod={setHoldingPeriod}
                    holdingPeriod={holdingPeriod}
                />
            )}

            {page === "building" && buildingModelUid && buildingModel?.reportedCrp?.carbonReductionPlanUid && (
                <CreateCustomPlanModal
                    modalIsOpen={createCustomPlanModalOpen}
                    onCancel={() => {
                        setPendingScenarioForNewPlan(null);
                        setCreateCustomPlanModalOpen(false);
                    }}
                    setModalIsOpen={setCreateCustomPlanModalOpen}
                    carbonReductionPlanUid={buildingModel.reportedCrp.carbonReductionPlanUid}
                    buildingModelUid={buildingModelUid}
                    onCreated={(newPlanId) => {
                        handleNewCustomPlanCreatedForChat(newPlanId).catch((err: unknown) => {
                            console.error("Failed after creating custom plan from chat:", err);
                        });
                    }}
                />
            )}

            <DangerousModal
                open={resetConfirmationOpen}
                onCancel={() => setResetConfirmationOpen(false)}
                onDelete={handleConfirmReset}
                loading={isClearingHistory}
                options={{
                    title: "Clear chat history?",
                    body: "This will permanently remove all chat history for this building. Do you wish to proceed?",
                    deleteButtonText: "Clear history",
                }}
                styleType="primary"
            />
        </>
    );
}

function ChatbotSidebarBuilding(props: ChatbotSidebarProps) {
    const buildingModelUid = useBuildingModelUid();
    const { buildingModel, bmcrp } = useSelectedBuildingModelCarbonReductionPlan();
    return (
        <ChatbotSidebarContent
            {...props}
            buildingContext={{
                buildingModelUid,
                buildingModel,
                bmcrp,
            }}
        />
    );
}

function ChatbotSidebarPortfolio(props: ChatbotSidebarProps) {
    return (
        <ChatbotSidebarContent
            {...props}
            buildingContext={{
                buildingModelUid: null,
                buildingModel: undefined,
                bmcrp: undefined,
            }}
        />
    );
}

function ChatbotSidebar(props: ChatbotSidebarProps) {
    return props.page === "building" ? <ChatbotSidebarBuilding {...props} /> : <ChatbotSidebarPortfolio {...props} />;
}

export default ChatbotSidebar;
